import { Search } from "@mui/icons-material";
import { ReactElement, useMemo, useState } from "react";
import { SOLUTION_TYPE_NAME_MAP, SolutionType } from "..";
import AlcoholDialog from "./AlcoholDialog";
import AwakeningDialog from "./AwakeningDialog";
import BathDialog from "./BathDialog";
import BeforeSleepMindDialog from "./BeforeSleepMindDialog";
import BlueLightDialog from "./BlueLightDialog";
import BodyConditionDialog from "./BodyConditionDialog";
import CaffeineDialog from "./CaffeineDialog";
import CircadianRhythmDialog from "./CircadianRhythmDialog";
import ContactDialog from "./ContactDialog";
import DreamDialog from "./DreamDialog";
import EfficiencyDialog from "./EfficiencyDialog";
import ExerciseDialog from "./ExerciseDialog";
import GeneralDialog from "./GeneralDialog";
import { HelpDialogButton, HelpDialogLayoutProps } from "./HelpDialogLayout";
import IdeasForLongerSleepDialog from "./IdeasForLongerSleepDialog";
import InitiatingSleepDialog from "./InitiatingSleepDialog";
import MorningLightDialog from "./MorningLight";
import NapDialog from "./NapDialog";
import NightMealDialog from "./NightMealDialog";
import RoomLightDialog from "./RoomLightDialog";
import SleepTimeDialog from "./SleepTimeDialog";
import SnoringDialog from "./SnoringDialog";
import ToiletDialog from "./ToiletDialog";
import TurnOverDialog from "./TurnOver";

function Dialog({
  type,
  ...props
}: { type: SolutionType } & HelpDialogLayoutProps) {
  const CONFIG: Record<SolutionType, ReactElement> = useMemo(
    () => ({
      efficiency: <EfficiencyDialog {...props} />, // 睡眠効率低下の一般的な原因を知る
      awakening: <AwakeningDialog {...props} />, // 中途覚醒が増える一般的な原因を知る
      toilet: <ToiletDialog {...props} />, // トイレに起きる原因を学ぶ
      initiating_sleep: <InitiatingSleepDialog {...props} />, // 寝付きが悪くなる一般的な原因を知る
      sleep_time: <SleepTimeDialog {...props} />, // 睡眠時間の重要性について知る
      ideas_for_longer_sleep: <IdeasForLongerSleepDialog {...props} />, // 睡眠時間を確保するアイデアを知る
      snoring: <SnoringDialog {...props} />,
      premature_morning_awakening: (
        <AwakeningDialog
          {...props}
          gaEventLabel={`${SOLUTION_TYPE_NAME_MAP.premature_morning_awakening}`}
          prematureMorningAwakening
        />
      ), // 早く目が覚めてしまう一般的な原因を知る
      contact: <ContactDialog {...props} />, // 相談先を知る
      dream: <DreamDialog {...props} />, // 夢に関して学ぶ
      turn_over: <TurnOverDialog {...props} />, // 寝返りの改善方法を学ぶ
      circadian_rhythm: <CircadianRhythmDialog {...props} />, // 睡眠と体内時計について知る
      nap: <NapDialog {...props} />, // 睡眠と仮眠の関係を学ぶ
      blue_light: <BlueLightDialog {...props} />, // 睡眠とスマホの関係を学ぶ
      alcohol: <AlcoholDialog {...props} />, // 睡眠と飲酒の関係を学ぶ
      caffeine: <CaffeineDialog {...props} />, // 睡眠とカフェインの関係を学ぶ
      night_meal: <NightMealDialog {...props} />, // 睡眠と夜食の関係を学ぶ
      bath: <BathDialog {...props} />, // 睡眠と入浴の関係を学ぶ
      room_light: <RoomLightDialog {...props} />, // 睡眠と部屋の明かりの関係を学ぶ
      morning_light: <MorningLightDialog {...props} />, // 睡眠と朝の光の関係を学ぶ
      before_sleep_mind: <BeforeSleepMindDialog {...props} />, // 睡眠と気分の関係を学ぶ
      body_condition: <BodyConditionDialog {...props} />, // 睡眠と体の調子の関係を学ぶ
      exercise: <ExerciseDialog {...props} />, // 睡眠と運動の関係を学ぶ
      general: <GeneralDialog {...props} />, // 一般的な睡眠に影響する生活習慣を知る
    }),
    [props]
  );
  return CONFIG[type];
}

export interface YourImprovementMethodButtonProps {
  solution: { caption: string; type: SolutionType };
}

export default function YourImprovementMethodButton({
  solution: { caption, type },
}: YourImprovementMethodButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <HelpDialogButton startIcon={<Search />} onClick={() => setOpen(true)}>
        {caption}
      </HelpDialogButton>
      <Dialog
        type={type}
        title={caption}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
